import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Grid, Skeleton } from 'antd';

import ContextBar from '../components/common/ContextBar';
import FormActionsFooter from '../components/common/FormActionsFooter';
import { confirmDestructive } from '../components/common/confirmDestructive';
import { Chart, preloadCharts } from '../components/charts/LazyCharts';
import { CardOptionLabel, buildMemberCardOptions } from '../components/cards/CardVisuals';
import { CHART_BAR } from '../components/icons';
import { chartPath } from '../components/reports/ChartRow';
import { deleteChartCopy } from '../components/reports/chartActions';
import FieldMessage from '../components/chartBuilder/FieldMessage';
import ItemsStep from '../components/chartBuilder/ItemsStep';
import MeasureStep from '../components/chartBuilder/MeasureStep';
import GroupStep from '../components/chartBuilder/GroupStep';
import FilterStep from '../components/chartBuilder/FilterStep';
import TimeStep from '../components/chartBuilder/TimeStep';
import ShowStep from '../components/chartBuilder/ShowStep';
import NameStep from '../components/chartBuilder/NameStep';
import {
	BUCKET_WORDS, builderView, cardLabel, chartFields, clearFormulasForCard, defaultMeasureLabel, draftFromSpec, emptyDraft,
	formReasons, namedDraft, newFormulaMeasure, newGroup, newMeasure, newPerItemMeasure, reconcile, refusalKey, removeMeasure, settleGoal,
	specFromDraft, stepOf, suggestName, withPlainMeasure,
} from '../components/chartBuilder/builderModel';
import { formulaRunMessages } from '../components/chartBuilder/formulaMessages';
import { chartableCardIds, suggestionCards, suggestionFor } from '../components/chartBuilder/suggestions';
import { resourceStateOf } from '../components/loading';
import { useCollectionContext } from '../contexts/CollectionContext';
import { useOptionalCardDefinitions } from '../contexts/CardDefinitionsContext';
import { useOptionalTags } from '../contexts/TagsContext';
import { useScreenShortcuts } from '../contexts/ShortcutsContext';
import useChartPreview from '../hooks/useChartPreview';
import useCollectionTags from '../hooks/useCollectionTags';
import { useDirtyForm } from '../hooks/useDirtyForm';
import { chartRefusal, createChart, deleteChart, getChart, updateChart } from '../services/charts';
import { collectionCrumbs } from '../utils/breadcrumbs';
import { chartFormWord } from '../utils/chartForms';
import { rangeLabel } from '../utils/chartRanges';
import { getCollectionFacetCardKeys, getCollectionFamilyCardIds, getCollectionMemberCardIds } from '../utils/collections';
import { hasSubCollections as collectionHasSubs, subCollectionsOf } from '../utils/collectionFamily';
import { escapeBelongsElsewhere } from '../utils/escapeOwner';
import { cardAndDescendantIds } from '../utils/facets';
import { normalizeId } from '../utils/ids';
import { buildFilterFields, tagsFilterField } from '../utils/kqlBuilder';
import { canReadCharts } from '../utils/permissions';
import { TOO_LONG_CODE, elementRefusalOf, withElementChoice } from '../utils/elementRefusal';
import { cardElementChain } from '../utils/chartSpecFields';

const OBJECT_ID = /^[0-9a-f]{24}$/i;
const lowerFirst = (text) => (typeof text === 'string' && text ? text.charAt(0).toLowerCase() + text.slice(1) : text);
const NO_MESSAGES = Object.freeze([]);
const NO_TAGS = Object.freeze([]);
const STEP_LEVEL = Object.freeze({ measures: 'measures', groupBy: 'groupBy', splitBy: 'splitBy', time: 'time', show: 'show' });

/** The one sentence for a chart that is gone, not yours or unreadable (ruled d26). */
const CHART_GONE = 'This chart is no longer available.';

// What dirty tracking compares: the question, its name and description —
// not whether the form was picked or recommended, nor how a goal was read in.
const serializeSnapshot = (snapshot) => {
	try {
		return JSON.stringify(snapshot, (key, value) => (key === 'chosen' || key === 'goalRaw' ? undefined : value));
	} catch {
		return String(snapshot);
	}
};

/**
 * THE CHART BUILDER — `/collections/:collectionId/charts/new` and
 * `/collections/:collectionId/charts/:chartId/edit` (charts A5b, plan §6.2).
 *
 * A chart is built as a question, read top to bottom as a sentence in the
 * side column: **Items** (the card, or every card), **Measure** (what to
 * compute), **Group by**, **Split by**, **Filter**, **Time** (the date and
 * its range), **Show as** with the form's **Options**, then its **Name**.
 * The preview is the main column: the same `ChartFrame` the chart page
 * draws (through the lazy boundary), run by the preview endpoint as the
 * question changes — debounced, one request at a time, a 429 or 503 said in
 * the frame's own words (hooks/useChartPreview).
 *
 * Every step offers only what the vendored capability table allows for the
 * chosen elements and says why where it disables something
 * (chartBuilder/builderModel). A choice a later change makes impossible — a
 * measure's element the new card lacks, a bucket the range would cut too
 * fine — is cleared with a one-line notice, never left dangling. A refusal
 * from the server — the preview's or a save's — is shown on the control its
 * `path` names.
 *
 * Save is the one filled primary (Cancel beside it; Go to Reports and, when
 * editing, Delete… behind the ⋯). A new chart is always the person's own
 * (POST, `scope: 'user'`); editing goes through PATCH with only what
 * changed. Unsaved changes are held when leaving (useDirtyForm).
 *
 * On a phone the preview sits on top at 240px, the steps fold — each says its
 * answer in one line while folded — and a Preview button brings the chart
 * back into view; Save is also at the foot (FormActionsFooter). Controls
 * meet the 44px floor (chart-builder.css).
 *
 * A SUGGESTION (charts A10): `/charts/new?suggestion=<key>` opens a new chart
 * with the question a Reports suggestion names already filled in
 * (chartBuilder/suggestions, rebuilt from the cards — the key says only which
 * kind and which card), unsaved, its name the one the builder would suggest.
 * Leaving it untouched asks nothing; Save makes it the person's own chart.
 * A key whose suggestion is not offered here (the card gone, or no longer
 * shaped for it) opens the empty question, and says so in one line.
 */
/** What the Items step says the chart counts, after the card: ", Line entries" (KPR-684), or nothing for items. */
function countedSummary(view) {
	const counting = view && view.counting;
	if (!counting || !counting.value) return '';
	const option = counting.options.find((o) => o.value === counting.value);
	return option ? `, ${option.label}` : '';
}

export default function ChartBuilderPage() {
	// One builder per question: a route that moves to another chart (or
	// collection) mounts a fresh one, so nothing of the last chart's draft —
	// its name above all — can be saved onto the next (the A5 review's L-4).
	const { chartId, collectionId } = useParams();
	const { resolvedCollectionId } = useCollectionContext() || {};
	// A new chart from another suggestion is another question too (A10).
	const [searchParams] = useSearchParams();
	const suggestion = chartId ? '' : searchParams.get('suggestion') || '';
	return <ChartBuilder key={`${resolvedCollectionId || collectionId || ''}|${chartId || 'new'}|${suggestion}`} />;
}

function ChartBuilder() {
	const { chartId: rawChartId } = useParams();
	const editing = rawChartId !== undefined;
	const [searchParams] = useSearchParams();
	// The suggestion a new chart starts from, read once (charts A10).
	const [suggestionKey] = useState(() => (editing ? null : searchParams.get('suggestion')));
	const chartId = editing && OBJECT_ID.test(rawChartId || '') ? rawChartId : null;
	const { collection, resolvedCollectionId, collectionError } = useCollectionContext() || {};
	const collectionId = resolvedCollectionId;
	const navigate = useNavigate();
	const { modal, message } = App.useApp();
	const phone = Grid.useBreakpoint().md === false;

	const allowed = Boolean(collection) && canReadCharts(collection);
	const reportsPath = `/collections/${collectionId}/reports`;

	// The chart drawer's code starts downloading with the page.
	useEffect(() => { preloadCharts().catch(() => {}); }, []);

	// ------------------------------------------------------- the card world
	const cardDefinitions = useOptionalCardDefinitions();
	const cards = useMemo(() => (Array.isArray(cardDefinitions?.cards) ? cardDefinitions.cards : []), [cardDefinitions?.cards]);
	const cardsById = useMemo(() => cardDefinitions?.cardsById || {}, [cardDefinitions?.cardsById]);
	const getElementsForCard = cardDefinitions?.getElementsForCard;
	const elementSetsById = cardDefinitions?.elementSetsById;
	const noteMissingCards = cardDefinitions?.noteMissingCards;
	const cardsLoading = Boolean(cardDefinitions?.loading);
	const subs = useMemo(() => subCollectionsOf(collection), [collection]);
	const withSubs = Boolean(collection) && collectionHasSubs(collection);

	// The cards a chart here may count: the collection's family, narrowed to
	// a card-scoped reader's cards (the API's card relation: usable here or
	// beneath, and readable — `card_unavailable` otherwise).
	const familyIds = useMemo(() => chartableCardIds(collection, cards, getCollectionFamilyCardIds(collection, cards)), [collection, cards]);
	const memberIds = useMemo(() => getCollectionMemberCardIds(collection, cards), [collection, cards]);

	const [refusedElements, setRefusedElements] = useState({});
	// `entries`: the repeating group whose entries the chart counts, by name
	// (KPR-684) — its columns measure, the item's own elements only group.
	// `timeOn`: the chart's date, which — a date column of that group
	// (keepr-api KPR-806) — gives each entry a latest value to be.
	const buildCtx = useCallback((cardId, entries = null, timeOn = null) => {
		const card = cardId ? cardsById[cardId] || null : null;
		const elements = card && typeof getElementsForCard === 'function' ? (getElementsForCard(cardId) || []).filter(Boolean) : [];
		// Every element its chain carries, unmerged: what a stored id names
		// where this card or an ancestor restates an inherited element
		// (KPR-238 P6, chartSpecFields cardElementChain — W2).
		const shadowed = card ? cardElementChain(cardId, cardsById, elementSetsById) : [];
		const descendants = card ? cardAndDescendantIds(cards, cardId).filter((id) => id !== cardId && familyIds.has(id)) : [];
		return {
			card,
			cardName: card ? cardLabel(card) : '',
			// Every element of the card, by name: what a per-item calculation reads (A8).
			elements,
			shadowed,
			fields: chartFields({ card, elements, cardHasDescendants: descendants.length > 0, hasSubCollections: withSubs, entries, time: timeOn }),
			entries: card ? entries : null,
			hasSubCollections: withSubs,
			refusedElements: cardId ? refusedElements[cardId] || {} : {},
		};
	}, [cards, cardsById, elementSetsById, getElementsForCard, familyIds, withSubs, refusedElements]);

	// ------------------------------------------------------- the chart (edit)
	const [stored, setStored] = useState(null);
	// A new chart from a suggestion waits for the cards it is made from.
	const [status, setStatus] = useState(editing || suggestionKey ? 'loading' : 'ready'); // loading · loaded · ready · gone · error · readonly
	const [loadToken, setLoadToken] = useState(0);
	useEffect(() => {
		if (!editing || !collectionId || !allowed) return undefined;
		if (!chartId) {
			setStatus('gone');
			return undefined;
		}
		let active = true;
		setStatus('loading');
		getChart(collectionId, chartId)
			.then((data) => {
				if (!active) return;
				setStored(data);
				setStatus(data && data.canEdit === true ? 'loaded' : 'readonly');
			})
			.catch((e) => {
				if (!active) return;
				const code = e?.response?.status;
				setStatus(code === 404 || code === 400 ? 'gone' : 'error');
			});
		return () => { active = false; };
	}, [editing, collectionId, chartId, allowed, loadToken]);

	// ------------------------------------------------------- the draft
	const [draft, setDraftState] = useState(() => (editing ? null : emptyDraft()));
	const draftRef = useRef(draft);
	const setDraft = (next) => { draftRef.current = next; setDraftState(next); };
	const [name, setName] = useState('');
	const [nameTouched, setNameTouched] = useState(false);
	const [description, setDescription] = useState('');
	const [readyToken, setReadyToken] = useState(0);
	const [notices, setNotices] = useState({});
	const [saveRefusal, setSaveRefusal] = useState(null);
	const [nameError, setNameError] = useState(null);
	const [descriptionError, setDescriptionError] = useState(null);
	const [formError, setFormError] = useState(null);
	const [attempted, setAttempted] = useState(false);
	const [saving, setSaving] = useState(false);
	const [openSteps, setOpenSteps] = useState(() => new Set(editing ? [] : ['items', 'measures']));
	const [tableOpen, setTableOpen] = useState(false);
	const baselineSpec = useRef(null);

	const storedCardId = normalizeId(stored?.spec?.card_id) || null;
	useEffect(() => {
		if (storedCardId && !cardsById[storedCardId] && typeof noteMissingCards === 'function') noteMissingCards([storedCardId]);
	}, [storedCardId, cardsById, noteMissingCards]);
	const storedCardSettled = !storedCardId || Boolean(cardsById[storedCardId])
		|| (typeof cardDefinitions?.isCardMissed === 'function' && cardDefinitions.isCardMissed(storedCardId));
	const initialized = useRef(false);
	useEffect(() => {
		if (status !== 'loaded' || initialized.current || cardsLoading || !storedCardSettled) return;
		initialized.current = true;
		const fromSpec = draftFromSpec(stored.spec);
		const ctx0 = buildCtx(fromSpec.cardId);
		// A field stored by id (KPR-238 P6) is worked on by its element's name;
		// one naming an element the card no longer holds is cleared, and said.
		const cleared = [];
		const raw = namedDraft(fromSpec, ctx0, cleared);
		// What it counts is known now, by name: its world, counting it.
		const ctx1 = buildCtx(raw.cardId, raw.entries, raw.time ? raw.time.on : null);
		const settled = settleGoal(raw, ctx1);
		baselineSpec.current = JSON.stringify(specFromDraft(settled, ctx1));
		setDraft(settled);
		setName(typeof stored.name === 'string' ? stored.name : '');
		setNameTouched(true);
		setDescription(typeof stored.description === 'string' ? stored.description : '');
		if (cleared.length) {
			setNotices((prev) => {
				const out = { ...prev };
				cleared.forEach((notice) => { out[notice.step] = [...(out[notice.step] || []), { text: notice.text }]; });
				return out;
			});
		}
		setReadyToken((n) => n + 1);
		setStatus('ready');
	}, [status, stored, cardsLoading, storedCardSettled, buildCtx]);

	// A suggestion's question, once the cards are known (charts A10). The
	// draft it seeds is the baseline (readyToken), so leaving it untouched
	// asks nothing.
	const seeded = useRef(false);
	useEffect(() => {
		if (editing || !suggestionKey || seeded.current || !collection || !allowed || cardsLoading) return;
		seeded.current = true;
		const found = suggestionFor(suggestionKey, {
			cards: suggestionCards(collection, cards, familyIds),
			getElementsForCard,
			allCards: cards,
			familyIds,
			hasSubCollections: withSubs,
		});
		if (found) {
			setDraft(found.draft);
			setOpenSteps(new Set(['items', 'measures', 'groupBy']));
		} else {
			setNotices((prev) => ({ ...prev, items: [{ text: "That suggestion isn't offered here any more, so the question starts empty." }] }));
		}
		setReadyToken((n) => n + 1);
		setStatus('ready');
	}, [editing, suggestionKey, collection, allowed, cardsLoading, cards, familyIds, getElementsForCard, withSubs]);

	const ctx = useMemo(() => buildCtx(draft ? draft.cardId : null, draft ? draft.entries : null, draft && draft.time ? draft.time.on : null), [buildCtx, draft]);
	const view = useMemo(() => (draft ? builderView(draft, ctx) : null), [draft, ctx]);
	const displayName = nameTouched ? name : (draft ? suggestName(draft, ctx) : '');

	// One change to the question: applied, reconciled against the card it now
	// names, the touched step's old notices replaced by what this change cleared.
	const update = useCallback((recipe, touched, extra = []) => {
		const current = draftRef.current;
		if (!current) return;
		const raw = recipe(current);
		if (!raw || raw === current) return;
		const reconciled = reconcile(raw, buildCtx(raw.cardId, raw.entries, raw.time ? raw.time.on : null));
		const next = reconciled.draft;
		const found = [...extra, ...reconciled.notices];
		draftRef.current = next;
		setDraftState(next);
		setSaveRefusal(null);
		setFormError(null);
		setNotices((prev) => {
			const out = { ...prev };
			if (touched) delete out[touched];
			found.forEach((notice) => { out[notice.step] = [...(out[notice.step] || []), { text: notice.text }]; });
			return out;
		});
	}, [buildCtx]);

	// ------------------------------------------------------- the preview
	// A saved calculation the run refuses (held, I-1) asks no preview either: it would only be refused.
	const spec = useMemo(() => (draft && view && !view.incomplete && !view.held ? specFromDraft(draft, ctx) : null), [draft, view, ctx]);
	const specKey = spec ? JSON.stringify(spec) : null;
	const draftCardReady = Boolean(draft) && !cardsLoading && (!draft.cardId || Boolean(cardsById[draft.cardId]) || storedCardSettled);
	// The person's picks for an element the filter could mean several of
	// (element ids step 3): asked with the preview and sent with the save.
	const [chartChoices, setChartChoices] = useState(null);
	const chartChoicesRef = useRef(null);
	chartChoicesRef.current = chartChoices;
	// A pick answers exactly the filter — on the card — it was made for: a
	// new filter or another card forgets it (the P4 review's R-SH1).
	const pickedFor = draft ? JSON.stringify([draft.cardId || null, draft.filter || '']) : null;
	useEffect(() => {
		chartChoicesRef.current = null;
		setChartChoices(null);
	}, [pickedFor]);
	const preview = useChartPreview({ collectionId, spec, enabled: allowed && status === 'ready' && draftCardReady, elementChoices: chartChoices });
	const current = Boolean(specKey) && preview.current;
	const previewRefusal = current ? preview.refusal : null;

	// An element the server refused as colliding with another card's is
	// marked in the pickers from then on (the vocabulary is the server's).
	useEffect(() => {
		const refusal = previewRefusal || saveRefusal;
		if (!refusal || refusal.code !== 'element_type_collision' || typeof refusal.element !== 'string' || !draft || !draft.cardId) return;
		setRefusedElements((prev) => {
			const forCard = prev[draft.cardId] || {};
			if (forCard[refusal.element] === refusal.message) return prev;
			return { ...prev, [draft.cardId]: { ...forCard, [refusal.element]: refusal.message || 'This element can\'t be charted here.' } };
		});
	}, [previewRefusal, saveRefusal, draft]);

	// ------------------------------------------------------- messages
	const refusal = saveRefusal || previewRefusal;
	// An element the filter could mean several of (`invalid_filter`, `cause`
	// `ambiguous_element`): said by the pick in the Filter step, not twice.
	// Too long once picked (a group repeats its condition): the server's
	// sentence, and the picks can be made again.
	const elementRefusal = useMemo(() => elementRefusalOf(refusal)
		|| (refusal && chartChoices && (refusal.cause === TOO_LONG_CODE || refusal.code === TOO_LONG_CODE)
			? { code: TOO_LONG_CODE, message: refusal.message || '', field: 'filter' }
			: null), [refusal, chartChoices]);
	const messages = useMemo(() => {
		const map = {};
		if (!view) return map;
		const add = (key, m) => { (map[key] = map[key] || []).push(m); };
		if (refusal && !elementRefusal) add(refusalKey(refusal.path, view), { tone: 'error', text: refusal.message || "This chart can't be drawn as it is." });
		// Every calculation's own problem beside its own formula (A8), not only the first.
		const said = new Set();
		(view.formulaIssues || []).forEach((issue) => {
			said.add(`${issue.key}|${issue.text}`);
			add(issue.key, { tone: issue.tone === 'hint' && attempted ? 'error' : issue.tone, text: issue.text });
		});
		if (view.incomplete && !said.has(`${view.incomplete.key}|${view.incomplete.message}`)) add(view.incomplete.key, { tone: attempted ? 'error' : 'hint', text: view.incomplete.message });
		if (current) (preview.warnings || []).forEach((w) => { if (w && w.message) add(refusalKey(w.path, view), { tone: 'note', text: w.message }); });
		// What the run says about a calculation — items it couldn't work out, a
		// chart whose formulas cost more than one run may spend — beside it too.
		if (current && draft) formulaRunMessages(preview.result, draft, view).forEach(({ key, message }) => add(key, message));
		if (draft && draft.cardId && !cardsLoading && !cardsById[draft.cardId]) add('card', { tone: 'error', text: "This chart's card isn't available in this collection any more. Choose another." });
		if (nameError) add('name', { tone: 'error', text: nameError });
		if (descriptionError) add('description', { tone: 'error', text: descriptionError });
		return map;
	}, [view, refusal, elementRefusal, attempted, current, preview.warnings, preview.result, draft, cardsLoading, cardsById, nameError, descriptionError]);
	const messagesFor = useCallback((key) => messages[key] || NO_MESSAGES, [messages]);

	// A refusal opens the step it belongs to (a folded step on a phone).
	const openStepOf = useCallback((key) => {
		const step = stepOf(key);
		setOpenSteps((prev) => (prev.has(step) ? prev : new Set([...prev, step])));
		return step;
	}, []);
	const refusalAt = refusal && view ? refusalKey(refusal.path, view) : null;
	useEffect(() => { if (refusalAt) openStepOf(refusalAt); }, [refusalAt, openStepOf]);

	// ------------------------------------------------------- filter fields
	const elementSets = cardDefinitions?.elementSets;
	const facetCardKeys = useMemo(() => getCollectionFacetCardKeys(collection), [collection]);
	const familyMembers = useMemo(() => (
		subs.length && collection ? [{ _id: normalizeId(collection._id), name: collection.name }, ...subs] : []
	), [collection, subs]);
	const { tags: tagVocabulary, loaded: tagsLoaded } = useCollectionTags(allowed ? collectionId : null);
	const myPrivateTags = useOptionalTags()?.tags || NO_TAGS;
	const filterMode = stored && stored.scope === 'collection' ? 'collection' : 'live';
	const filterCardIds = useMemo(() => {
		if (!draft || !draft.cardId) return familyIds.size ? familyIds : null;
		return new Set(cardAndDescendantIds(cards, draft.cardId).filter((id) => id === draft.cardId || familyIds.has(id)));
	}, [draft, cards, familyIds]);
	const filterFields = useMemo(() => [
		...buildFilterFields({ cards, cardIds: filterCardIds, getElementsForCard, facetCardKeys, elementSets, family: familyMembers }),
		// A chart pinned for everyone is checked in collection mode (ids only,
		// never a private tag); one of your own in live mode, as your own list
		// (the A5 review's M-2: the save was refused private_tag_not_allowed).
		tagsFilterField({ tags: tagVocabulary || NO_TAGS, privateTags: myPrivateTags, itemTitles: [], mode: filterMode, loaded: tagsLoaded, quickFilter: false }),
	], [cards, filterCardIds, getElementsForCard, facetCardKeys, elementSets, familyMembers, tagVocabulary, myPrivateTags, tagsLoaded, filterMode]);

	// ------------------------------------------------------- the card picker
	const cardOptions = useMemo(() => buildMemberCardOptions(cards, familyIds).map(({ value, card, depth, searchText }) => {
		const subOnly = withSubs && !memberIds.has(value);
		const disabled = subOnly && draft && draft.subCollections === false;
		return {
			value,
			label: <CardOptionLabel card={card} depth={depth} />,
			searchText,
			disabled,
			reason: disabled ? 'Used only in sub-collections: turn on Include sub-collections to chart it.' : null,
		};
	}), [cards, familyIds, memberIds, withSubs, draft]);

	// ------------------------------------------------------- handlers
	const onCard = (cardId) => {
		const d = draftRef.current;
		if (!d || d.cardId === cardId) return;
		// A calculation that reads what the new card lacks is cleared, with a line.
		const cleared = clearFormulasForCard({ ...d, cardId }, buildCtx(cardId, d.entries, d.time ? d.time.on : null), buildCtx(d.cardId, d.entries, d.time ? d.time.on : null));
		update(() => cleared.draft, 'items', cleared.notices);
	};
	// What the chart counts (KPR-684): its items (null), or a group's entries.
	const onEntries = (entries) => update((d) => (d.entries === entries ? d : { ...d, entries }), 'items');
	const onSubCollections = (on) => {
		const d = draftRef.current;
		// A card used only in sub-collections can't be charted without them (the
		// server's card_needs_sub_collections): the chart counts every card instead.
		if (d && !on && d.cardId && withSubs && !memberIds.has(d.cardId)) {
			const text = `${cardLabel(cardsById[d.cardId]) || 'That card'} is used only in sub-collections, so the chart counts every card now.`;
			update((x) => ({ ...x, subCollections: false, cardId: null }), 'items', [{ step: 'items', text }]);
			return;
		}
		update((x) => ({ ...x, subCollections: on }), 'items');
	};
	const onMeasure = (index, patch) => update((d) => ({
		...d,
		measures: d.measures.map((m, i) => {
			if (i !== index) return m;
			const next = { ...m, ...patch };
			// A calculation's verb, once picked, is the person's (it no longer follows the result's default).
			if (patch.op !== undefined && typeof m.perItem === 'string') next.opChosen = true;
			if (patch.op !== undefined && patch.op !== m.op && patch.op === 'count') Object.assign(next, { element: null, unit: null, currency: null, blanks: 'omit' });
			if (patch.element !== undefined && patch.element !== m.element) Object.assign(next, { unit: null, currency: null });
			return next;
		}),
	}), 'measures');
	// A new measure goes above the formulas across measures, so they can read it.
	const onAddMeasure = () => update((d) => ({ ...d, measures: withPlainMeasure(d.measures, newMeasure(d.measures)) }), 'measures');
	const onCalculate = () => update((d) => ({ ...d, measures: withPlainMeasure(d.measures, newPerItemMeasure(d.measures)) }), 'measures');
	const onAddFormula = () => update((d) => ({ ...d, measures: [...d.measures, newFormulaMeasure(d.measures)] }), 'measures');
	const onRemoveMeasure = (index) => {
		const d = draftRef.current;
		if (!d) return;
		// A formula that read it is cleared, never left naming a measure that is gone.
		const removed = removeMeasure(d, index, buildCtx(d.cardId, d.entries, d.time ? d.time.on : null));
		update(() => removed.draft, 'measures', removed.notices);
	};
	const onField = (which, fieldName) => update((d) => {
		if (!fieldName) return which === 'groupBy' ? { ...d, groupBy: null, splitBy: null } : { ...d, splitBy: null };
		const field = buildCtx(d.cardId, d.entries, d.time ? d.time.on : null).fields.find((f) => f.name === fieldName);
		return { ...d, [which]: newGroup(field) };
	}, which);
	const onGroup = (which, patch) => update((d) => (d[which] ? { ...d, [which]: { ...d[which], ...patch } } : d), which);
	const onFilter = (text) => update((d) => (d.filter === text ? d : { ...d, filter: text }), 'filter');
	const onTime = (patch) => update((d) => {
		const time = { ...d.time, ...patch };
		if (patch.on !== undefined && patch.on !== d.time.on) time.multi = null;
		return { ...d, time };
	}, patch.compare !== undefined ? 'show' : 'time');
	const onShow = (patch) => update((d) => ({ ...d, show: { ...d.show, ...patch } }), 'show');
	const onName = (value) => { setName(value); setNameTouched(true); setNameError(null); setFormError(null); };
	const onDescription = (value) => { setDescription(value); setDescriptionError(null); setFormError(null); };

	// ------------------------------------------------------- unsaved changes
	const snapshot = useMemo(() => ({ draft, name: displayName, description }), [draft, displayName, description]);
	const { isDirty, resetDirty, allowNavigation, guardDiscard } = useDirtyForm(snapshot, {
		baselineKey: readyToken,
		serializer: serializeSnapshot,
		enabled: Boolean(draft),
	});
	const leaveTo = editing && chartId ? chartPath(collectionId, chartId) : reportsPath;
	const cancel = () => guardDiscard(() => navigate(leaveTo));

	// ------------------------------------------------------- save
	const scrollToStep = (step) => {
		const el = typeof document !== 'undefined' ? document.querySelector(`.chart-builder__step[data-step="${step}"]`) : null;
		if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start', behavior: 'smooth' });
	};
	const save = async () => {
		// Only a builder that is ready and that this person may use saves —
		// the keyboard included (the A5 review's L-3, L-4) — and nothing
		// unchanged, as the greyed button says.
		if (!draft || saving || !allowed || status !== 'ready' || (editing && !isDirty)) return;
		setAttempted(true);
		setFormError(null);
		const trimmed = displayName.trim();
		if (!trimmed) {
			setNameError('Give the chart a name.');
			scrollToStep(openStepOf('name'));
			return;
		}
		const body = specFromDraft(draft, ctx);
		// What the person did not change never stops a save (the A8 review's
		// I-1): a chart whose question is as it was opened sends its name and
		// description alone, whatever the builder thinks of its question — the
		// server took that question already. A changed one must be whole.
		const questionAsOpened = editing && JSON.stringify(body) === baselineSpec.current;
		if (view.incomplete && !questionAsOpened) {
			scrollToStep(openStepOf(view.incomplete.key));
			return;
		}
		setSaving(true);
		try {
			let saved;
			if (editing) {
				const patch = {};
				if (trimmed !== (stored?.name || '')) patch.name = trimmed;
				if (description.trim() !== (stored?.description || '')) patch.description = description.trim();
				if (JSON.stringify(body) !== baselineSpec.current) patch.spec = body;
				saved = Object.keys(patch).length ? await updateChart(collectionId, chartId, { ...patch, ...(chartChoicesRef.current ? { elementChoices: chartChoicesRef.current } : {}) }) : stored;
			} else {
				saved = await createChart(collectionId, { name: trimmed, description: description.trim(), spec: body, ...(chartChoicesRef.current ? { elementChoices: chartChoicesRef.current } : {}) });
			}
			resetDirty();
			message.success(editing ? `Saved "${trimmed}".` : `Made "${trimmed}", a chart only you see. A manager can pin it for everyone.`);
			const id = normalizeId(saved?._id) || chartId;
			navigate(id ? chartPath(collectionId, id) : reportsPath);
		} catch (e) {
			const code = e?.response?.status;
			const data = e?.response?.data || {};
			if (code === 409 && data.code === 'chart_name_taken') {
				setNameError(data.message || 'A chart already has this name here. Choose another.');
				scrollToStep(openStepOf('name'));
			} else if (code === 400 && (data.code === 'invalid_name' || data.code === 'invalid_description')) {
				if (data.code === 'invalid_name') setNameError(data.message || 'The name can\'t be used.');
				else setDescriptionError(data.message || 'The description can\'t be used.');
				scrollToStep(openStepOf('name'));
			} else if (code === 400 && typeof data.code === 'string' && typeof data.path === 'string') {
				setSaveRefusal({ ...data, message: typeof data.message === 'string' ? data.message : '' });
				setFormError(`The chart wasn't saved: ${data.message || 'one of its choices can\'t be used.'}`);
				scrollToStep(openStepOf(refusalKey(data.path, view)));
			} else if (code === 404) {
				setFormError(CHART_GONE);
			} else {
				setFormError(chartRefusal(e, "The chart wasn't saved. Trying again may help."));
			}
		} finally {
			setSaving(false);
		}
	};
	const saveRef = useRef(save);
	saveRef.current = save;
	// A pick answers the refusal it was offered for: the preview asks again
	// with it, and a refused save is made again.
	const pickElement = (value) => {
		if (!elementRefusal || elementRefusal.code !== 'ambiguous_element') return;
		const fromSave = Boolean(saveRefusal);
		const next = withElementChoice(chartChoicesRef.current || {}, elementRefusal, value);
		chartChoicesRef.current = next;
		setChartChoices(next);
		setSaveRefusal(null);
		setFormError(null);
		if (fromSave) setTimeout(() => saveRef.current(), 0);
	};
	// Too long once picked: forget the picks, and the preview asks again.
	const repickElements = () => {
		chartChoicesRef.current = null;
		setChartChoices(null);
		setSaveRefusal(null);
		setFormError(null);
	};
	const cancelRef = useRef(cancel);
	cancelRef.current = cancel;

	useScreenShortcuts('Chart builder', [
		{ keys: ['mod', 'enter'], label: 'Save the chart' },
		{ keys: ['esc'], label: 'Cancel' },
	]);
	useEffect(() => {
		const handler = (event) => {
			if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
				event.preventDefault();
				saveRef.current();
			} else if (event.key === 'Escape') {
				if (escapeBelongsElsewhere(event)) return;
				event.preventDefault();
				cancelRef.current();
			}
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, []);

	const confirmDelete = () => {
		confirmDestructive(modal, {
			...deleteChartCopy(stored),
			okText: 'Delete',
			onOk: async () => {
				try {
					await deleteChart(collectionId, chartId);
					allowNavigation();
					message.success(`Deleted "${stored?.name || 'the chart'}". History can restore it.`);
					navigate(reportsPath);
				} catch (e) {
					message.error(chartRefusal(e, 'Could not delete the chart.'));
				}
			},
		});
	};

	// ------------------------------------------------------- the bar
	const ready = status === 'ready' && Boolean(draft);
	const clean = editing && !isDirty;
	// The buttons, once; the bar adds its status text ("No changes"), the
	// footer carries the buttons alone (FormActionsFooter's contract).
	const formActions = ready ? (
		<>
			<Button onClick={cancel}>Cancel</Button>
			<Button type='primary' onClick={save} loading={saving} disabled={clean}>Save</Button>
		</>
	) : null;
	const barActions = ready ? (
		<>
			{clean ? <span className='item-form-draft'>No changes</span> : null}
			{formActions}
		</>
	) : null;
	const menuActions = [
		// No door to Reports for someone charts are not offered to (d14; the A5 review's L-3).
		allowed ? { key: 'reports', group: 'navigate', icon: <CHART_BAR />, label: 'Go to Reports', onClick: () => guardDiscard(() => navigate(reportsPath)) } : null,
		editing && ready ? { key: 'delete', group: 'destructive', glyph: 'delete', label: 'Delete…', onClick: confirmDelete } : null,
	].filter(Boolean);
	const title = editing ? (stored?.name || (status === 'loading' ? 'Loading chart…' : 'Chart')) : 'New chart';
	const bar = (
		<ContextBar
			breadcrumb={collectionCrumbs(collection, collectionId, { label: 'Reports', to: reportsPath })}
			title={title}
			actions={barActions}
			menuActions={menuActions}
		/>
	);
	// ------------------------------------------------------- the page
	const collectionGone = !collection && resourceStateOf(collectionError) === 'not-found';
	let body = null;
	if (collection && !allowed) {
		body = <p className='chart-page__empty'>Charts are for people who read this collection themselves, so there is none to build here.</p>;
	} else if (collectionGone || status === 'gone') {
		body = (
			<p className='chart-page__empty'>
				{`${CHART_GONE} `}
				<Link to={reportsPath}>Go to Reports</Link>
			</p>
		);
	} else if (status === 'readonly') {
		body = (
			<p className='chart-page__empty'>
				{"You can't edit this chart here: a chart of your own is changed by you, one pinned for everyone by a manager, and one from the collection above where it is defined. "}
				<Link to={chartPath(collectionId, chartId)}>Open the chart</Link>
			</p>
		);
	} else if (status === 'error' || (!collection && collectionError)) {
		body = (
			<div className='chart-page__error' role='alert'>
				<span>This chart couldn't be loaded. Trying again may help.</span>
				<Button size='small' onClick={() => setLoadToken((n) => n + 1)}>Try again</Button>
			</div>
		);
	} else if (!ready || !view) {
		body = <Skeleton active paragraph={{ rows: 8 }} title={false} />;
	}

	if (body) {
		return (
			<div className='chart-builder'>
				{bar}
				<div className='chart-page__body'>{body}</div>
			</div>
		);
	}

	const foldable = phone;
	const stepProps = (step, summary) => ({
		foldable,
		open: !foldable || openSteps.has(step),
		onToggle: () => setOpenSteps((prev) => {
			const next = new Set(prev);
			if (next.has(step)) next.delete(step);
			else next.add(step);
			return next;
		}),
		notices: notices[step] || NO_MESSAGES,
		messages: STEP_LEVEL[step] ? messagesFor(STEP_LEVEL[step]) : NO_MESSAGES,
		summary,
	});
	const groupSummary = (group) => {
		if (!group) return 'Nothing';
		const field = ctx.fields.find((f) => f.name === group.on);
		const label = field ? field.label : group.on;
		return group.bucket && BUCKET_WORDS[group.bucket] ? `${label}, by ${BUCKET_WORDS[group.bucket].toLowerCase()}` : label;
	};
	const timeField = ctx.fields.find((f) => f.name === draft.time.on);
	const summaries = {
		items: `${ctx.card ? ctx.cardName : 'Every card'}${countedSummary(view)}${view.subCollections && draft.subCollections ? ', with sub-collections' : ''}`,
		measures: draft.measures.map((m) => (m.label && m.label.trim()) || defaultMeasureLabel(m, ctx, draft)).join(', '),
		groupBy: groupSummary(draft.groupBy),
		splitBy: groupSummary(draft.groupBy ? draft.splitBy : null),
		filter: draft.filter ? 'Filtered' : 'No filter',
		time: timeField ? `${timeField.label} · ${rangeLabel(draft.time.range)}` : 'No date',
		show: chartFormWord(draft.show.type),
		name: displayName || 'No name yet',
	};

	const previewChart = { name: displayName || 'New chart', spec: (current && preview.previewSpec) || spec || specFromDraft(draft, ctx) };
	const previewElements = {
		group: draft.groupBy ? ctx.fields.find((f) => f.name === draft.groupBy.on)?.element || null : null,
		split: draft.splitBy ? ctx.fields.find((f) => f.name === draft.splitBy.on)?.element || null : null,
		// The preview's spec names elements by id once the server writes ids (KPR-238 P6).
		list: ctx.elements,
		chain: ctx.shadowed,
	};
	let previewBody;
	if (!spec) {
		let waiting = 'The chart appears here once the question is whole.';
		if (view.incomplete) waiting = `${view.incomplete.message} ${waiting}`;
		else if (view.held) waiting = `This chart can't be drawn yet: ${lowerFirst(view.held.text)} The reason is beside the formula.`;
		previewBody = <p className='chart-builder__preview-empty'>{waiting}</p>;
	} else if (previewRefusal) {
		previewBody = (
			<div className='chart-builder__preview-refused' role='status'>
				<span>{`This question can't be charted as it is: ${previewRefusal.message || 'one of its choices can\'t be used.'}`}</span>
				<span className='chart-builder__hint'>The choice is marked in the question.</span>
			</div>
		);
	} else {
		previewBody = (
			<Chart
				chart={previewChart}
				result={preview.result}
				loading={preview.loading}
				collectionId={collectionId}
				cardName={ctx.card ? ctx.cardName : undefined}
				elements={previewElements}
				headingLevel={2}
				height={phone ? 240 : 320}
				showTable={tableOpen}
				onToggleTable={() => setTableOpen((open) => !open)}
			/>
		);
	}
	const generalMessages = messagesFor('general');

	return (
		<div className={`chart-builder${phone ? ' chart-builder--phone' : ''}`}>
			{bar}
			{formError ? <Alert type='error' showIcon role='alert' className='chart-builder__error' message={formError} /> : null}
			<FieldMessage messages={generalMessages} id='chart-builder-general-messages' />
			<div className='chart-builder__columns'>
				<section className='chart-builder__preview' aria-label='Preview' id='chart-builder-preview'>
					<div className='chart-builder__preview-head'>
						<span className='micro-label'>Preview</span>
						{/* "Refresh", the verb the chart page uses and the frame's sentences name. */}
						<Button size='small' className='chart-builder__rerun' onClick={preview.run} disabled={!spec || preview.inFlight}>
							Refresh
						</Button>
					</div>
					{previewBody}
				</section>
				<div className='chart-builder__question'>
					<ItemsStep draft={draft} view={view} cardOptions={cardOptions} onCard={onCard} onEntries={onEntries} onSubCollections={onSubCollections} messagesFor={messagesFor} step={stepProps('items', summaries.items)} />
					<MeasureStep draft={draft} view={view} onMeasure={onMeasure} onAdd={onAddMeasure} onCalculate={onCalculate} onAddFormula={onAddFormula} onRemove={onRemoveMeasure} messagesFor={messagesFor} step={stepProps('measures', summaries.measures)} />
					<GroupStep which='groupBy' number={3} draft={draft} view={view} onGroup={onGroup} onField={onField} messagesFor={messagesFor} step={stepProps('groupBy', summaries.groupBy)} />
					<GroupStep which='splitBy' number={4} draft={draft} view={view} onGroup={onGroup} onField={onField} messagesFor={messagesFor} step={stepProps('splitBy', summaries.splitBy)} />
					<FilterStep
						draft={draft}
						fields={filterFields}
						collectionId={collectionId}
						onFilter={onFilter}
						messagesFor={messagesFor}
						step={stepProps('filter', summaries.filter)}
						elementRefusal={elementRefusal}
						onElementPick={pickElement}
						onElementRepick={repickElements}
					/>
					<TimeStep draft={draft} view={view} onTime={onTime} messagesFor={messagesFor} step={stepProps('time', summaries.time)} />
					<ShowStep draft={draft} view={view} reasons={formReasons(draft, ctx, view.forms)} onShow={onShow} onTime={onTime} messagesFor={messagesFor} step={stepProps('show', summaries.show)} />
					<NameStep number={9} name={displayName} description={description} onName={onName} onDescription={onDescription} messagesFor={messagesFor} step={stepProps('name', summaries.name)} />
					<FormActionsFooter>{formActions}</FormActionsFooter>
				</div>
			</div>
			{phone ? (
				<Button
					className='chart-builder__to-preview'
					onClick={() => {
						const el = document.getElementById('chart-builder-preview');
						if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start', behavior: 'smooth' });
					}}
				>
					Preview
				</Button>
			) : null}
		</div>
	);
}
